import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Form, Dropdown, Alert } from 'react-bootstrap';
import { FaCheckCircle, FaChevronDown, FaQuestionCircle } from 'react-icons/fa';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { BsDash, BsPlus } from 'react-icons/bs';
import { useNavigate, useLocation } from 'react-router-dom';
import AgencyNavbar from '../../Pages/Navbar/AgencyNavbar';
import Swal from 'sweetalert2';
import BaseUrl from '../../ApiServices/BaseUrl';

const AgencyAboutYourself = () => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user'));
    const userId = user?.id;

    const location = useLocation();
    const temporaryId = location.state?.temporaryId;
    console.log('receiving temporary_id:', temporaryId);

    // Refs for form fields
    const formRef = useRef(null);
    const nameRef = useRef(null);
    const emailRef = useRef(null);
    const whatsappRef = useRef(null);
    const phoneRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [workCountry, setWorkCountry] = useState('');
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    // Enhanced country to phone code mapping
    const countryPhoneCodeMap = {
        'UAE': '+971',
        'Dubai': '+971',
        'Crotia': '+385',
        'Saudi Arabia': '+966',
        'Qatar': '+974',
        'Turkey': '+90',
        'Kuwait': '+965',
        'Bahrain': '+973',
        'Oman': '+968',
        'Brunei': '+673',
        'Singapore': '+65',
        'Malaysia': '+60',
        'Hong Kong': '+852',
        'Japan': '+81',
        'South Korea': '+82',
        'Taiwan': '+886',
        'Germany': '+49',
        'Netherlands': '+31',
        'United Kingdom': '+44',
        'Italy': '+39',
        'Spain': '+34',
        'France': '+33',
        'Poland': '+48',
        'Portugal': '+351',
        'Romania': '+40',
        'Hungary': '+36',
        'Canada': '+1',
        'United States': '+1',
        'Australia': '+61',
        'New Zealand': '+64',
        'Any': 'Any'
    };

    // Initialize formData
    const [formData, setFormData] = useState({
        looking_worker_for: 'For family',
        name: '',
        nationality: '',
        organization_name: '',
        adults: 0,
        children: 0,
        type_of_house: '',
        rooms: 0,
        bathrooms: 0,
        have_domestic_worker: false,
        worker_nationality: '',
        have_pets: false,
        contact_source: '',
        email_id: '',
        whatsapp_number_country_code: '',
        whatsapp_number: '',
        phone_country_code: '',
        phone_number: '',
        offer_for_selected_candidates: ["Free food", "Accommodation", "Transportation", "Free Medical", "Yearly holiday", "Paid Leave", "Weekly off", "2 years contract"],
        country_currency: 'QAR (Qatari Riyal)',
        minimum_monthly_salary: '',
        maximum_monthly_salary: '',
        negotiable: false,
        min_salary_local: '',
        min_salary_usd: '',
        min_salary_qar: '',
        max_salary_local: '',
        max_salary_usd: '',
        max_salary_qar: ''
    });

    const isCompanyFlow = formData.looking_worker_for === 'For company';

    // Nationality options
    const nationalityOptions = [
        "UAE",
        "Crotia",
        "Dubai",
        "Saudi",
        "Qatar",
        "Turkey",
        "Kuwait",
        "Bahrain",
        "Oman",
        "Brunei",
        "Singapore",
        "Malaysia",
        "Hong Kong",
        "Japan",
        "South Korea",
        "Taiwan",
        "Germany",
        "Netherlands",
        "UK",
        "Italy",
        "Spain",
        "France",
        "Poland",
        "Portugal",
        "Romania",
        "Hungary",
        "Canada",
        "United States",
        "Australia",
        "New Zealand",
    ];

    const workerNationalityOptions = ["Philippines", "India", "Nepal", "Other"];
    const currencyOptions = [
        'AED (UAE Dirham)',
        'SAR (Saudi Riyal)',
        'QAR (Qatari Riyal)',
        'OMR (Omani Rial)',
        'KWD (Kuwaiti Dinar)',
        'BHD (Bahraini Dinar)',
        'JOD (Jordan)',
        'LBN (Lebanon)',
        'SGD (Singapore Dollor)',
        'HKD (Hong Kong)',
        'MYD (Malaysia)',
        'TWD (Taiwan)',
        'JPN (Japan)',
        'KRW (South Korea)',
        'BND (Brunei)',
        'USD (US Dollar)',
        'EUR (Euro)',
        'TRY (Turkey - Turkish Lira)',
    ];

    // Default country options
    const defaultCountryOptions = [
        { code: '+971', name: 'UAE', display: '+971 (UAE)' },
        { code: '+966', name: 'Saudi Arabia', display: '+966 (Saudi Arabia)' },
        { code: '+91', name: 'India', display: '+91 (India)' },
        { code: '+94', name: 'Sri Lanka', display: '+94 (Sri Lanka)' }
    ];

    // Currency conversion rates
    const conversionRates = {
        'USD': 1,
        'AED': 3.67,
        'SAR': 3.75,
        'QAR': 3.64,
        'OMR': 0.38,
        'KWD': 0.31,
        'BHD': 0.38,
        'EUR': 0.85,
        'GBP': 0.73,
        'SGD': 1.35
    };

    // Validation rules
    const validateField = (name, value) => {
        const newErrors = { ...errors };

        switch (name) {
            case 'min_salary_local':
            case 'min_salary_usd':
            case 'min_salary_qar':
                if (value && formData.max_salary_local && parseFloat(value) > parseFloat(formData.max_salary_local)) {
                    newErrors.min_salary_local = 'Minimum salary cannot be greater than maximum salary';
                } else if (value && parseFloat(value) < 0) {
                    newErrors.min_salary_local = 'Salary cannot be negative';
                } else {
                    delete newErrors.min_salary_local;
                }
                break;

            case 'max_salary_local':
            case 'max_salary_usd':
            case 'max_salary_qar':
                if (value && formData.min_salary_local && parseFloat(value) < parseFloat(formData.min_salary_local)) {
                    newErrors.max_salary_local = 'Maximum salary cannot be less than minimum salary';
                } else if (value && parseFloat(value) < 0) {
                    newErrors.max_salary_local = 'Salary cannot be negative';
                } else {
                    delete newErrors.max_salary_local;
                }
                break;

            case 'name':
                if (!value.trim()) newErrors.name = 'Name is required';
                else if (value.trim().length < 2) newErrors.name = 'Name should be at least 2 characters long';
                else delete newErrors.name;
                break;

            case 'nationality':
                if (!value) newErrors.nationality = 'Please select your nationality';
                else delete newErrors.nationality;
                break;

            case 'email_id':
                if (!value.trim()) newErrors.email_id = 'Email is required';
                else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    newErrors.email_id = 'Please enter a valid email address';
                } else {
                    delete newErrors.email_id;
                }
                break;

            case 'whatsapp_number':
                if (!value.trim()) newErrors.whatsapp_number = 'WhatsApp Number is required';
                else if (value && !/^\d+$/.test(value.replace(/\s/g, ''))) {
                    newErrors.whatsapp_number = 'Please enter a valid WhatsApp number';
                } else if (value && !formData.whatsapp_number_country_code) {
                    newErrors.whatsapp_number_country_code = 'Please select country code for WhatsApp';
                } else {
                    delete newErrors.whatsapp_number;
                    if (value) delete newErrors.whatsapp_number_country_code;
                }
                break;

            case 'phone_number':
                if (!value.trim()) newErrors.phone_number = 'Phone Number is required';
                else if (value && !/^\d+$/.test(value.replace(/\s/g, ''))) {
                    newErrors.phone_number = 'Please enter a valid phone number';
                } else if (value && !formData.phone_country_code) {
                    newErrors.phone_country_code = 'Please select country code for phone';
                } else {
                    delete newErrors.phone_number;
                    if (value) delete newErrors.phone_country_code;
                }
                break;

            case 'country_currency':
                if (!value) newErrors.country_currency = 'Please select currency';
                else delete newErrors.country_currency;
                break;

            default:
                break;
        }

        return newErrors;
    };

    // Validate entire form
    const validateForm = () => {
        const newErrors = {};

        // Required fields
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.nationality) newErrors.nationality = 'Please select your nationality';
        if (!formData.email_id) newErrors.email_id = 'Email is required';
        if (!formData.country_currency) newErrors.country_currency = 'Please select currency';

        // Conditional validation for domestic worker (family flow only)
        if (!isCompanyFlow && formData.have_domestic_worker && !formData.worker_nationality) {
            newErrors.worker_nationality = 'Please select worker nationality';
        }

        // Email validation
        if (formData.email_id && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_id)) {
            newErrors.email_id = 'Please enter a valid email address';
        }

        // Phone number validation
        if (!formData.whatsapp_number) newErrors.whatsapp_number = 'WhatsApp Number is required';
        if (formData.whatsapp_number && !/^\d+$/.test(formData.whatsapp_number.replace(/\s/g, ''))) {
            newErrors.whatsapp_number = 'Please enter a valid WhatsApp number';
        }
        if (formData.whatsapp_number && !formData.whatsapp_number_country_code) {
            newErrors.whatsapp_number_country_code = 'Please select country code for WhatsApp';
        }

        if (!formData.phone_number) newErrors.phone_number = 'Phone Number is required';
        if (formData.phone_number && !/^\d+$/.test(formData.phone_number.replace(/\s/g, ''))) {
            newErrors.phone_number = 'Please enter a valid phone number';
        }
        if (formData.phone_number && !formData.phone_country_code) {
            newErrors.phone_country_code = 'Please select country code for phone';
        }

        // Salary validation
        if (formData.min_salary_local && formData.max_salary_local &&
            parseFloat(formData.min_salary_local) > parseFloat(formData.max_salary_local)) {
            newErrors.min_salary_local = 'Minimum salary cannot be greater than maximum salary';
        }

        if (formData.min_salary_local && parseFloat(formData.min_salary_local) < 0) {
            newErrors.min_salary_local = 'Salary cannot be negative';
        }

        if (formData.max_salary_local && parseFloat(formData.max_salary_local) < 0) {
            newErrors.max_salary_local = 'Salary cannot be negative';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle field blur for validation
    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
        setErrors(validateField(field, formData[field]));
    };

    // Focus on first error field
    const focusFirstError = () => {
        const errorFields = Object.keys(errors);
        if (errorFields.length > 0) {
            const firstErrorField = errorFields[0];
            switch (firstErrorField) {
                case 'name':
                    nameRef.current?.focus();
                    break;
                case 'email_id':
                    emailRef.current?.focus();
                    break;
                case 'whatsapp_number':
                    whatsappRef.current?.focus();
                    break;
                case 'phone_number':
                    phoneRef.current?.focus();
                    break;
                default:
                    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    break;
            }
        }
    };

    const parseArray = (arrayString) => {
        if (!arrayString) return [];

        try {
            const parsed = JSON.parse(arrayString);
            if (typeof parsed === 'string') {
                const secondParse = JSON.parse(parsed);
                if (typeof secondParse === 'string') {
                    return JSON.parse(secondParse);
                }
                return secondParse;
            }
            return parsed;
        } catch (e) {
            console.error('Error parsing array:', e);
            if (Array.isArray(arrayString)) {
                return arrayString.filter(item => typeof item === 'string' && item.length > 0 && !['"', '[', ']', '\\'].includes(item));
            }
            return [];
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Update currency conversions when currency changes
    useEffect(() => {
        const updateCurrencyConversions = () => {
            const currencyCode = extractCurrencyCode(formData.country_currency);

            if (currencyCode && formData.min_salary_local) {
                const minUSD = convertCurrency(formData.min_salary_local, currencyCode, 'USD');
                const minQAR = convertCurrency(formData.min_salary_local, currencyCode, 'QAR');

                setFormData(prev => ({
                    ...prev,
                    min_salary_usd: minUSD,
                    min_salary_qar: minQAR
                }));
            }

            if (currencyCode && formData.max_salary_local) {
                const maxUSD = convertCurrency(formData.max_salary_local, currencyCode, 'USD');
                const maxQAR = convertCurrency(formData.max_salary_local, currencyCode, 'QAR');

                setFormData(prev => ({
                    ...prev,
                    max_salary_usd: maxUSD,
                    max_salary_qar: maxQAR
                }));
            }
        };

        updateCurrencyConversions();
    }, [formData.country_currency, formData.min_salary_local, formData.max_salary_local]);

    const getCountryCode = (countryName) => {
        if (!countryName) return '';

        const cleanCountryName = countryName.trim();
        console.log('Looking for country code for:', cleanCountryName);

        if (countryPhoneCodeMap[cleanCountryName]) {
            console.log('Found direct mapping:', countryPhoneCodeMap[cleanCountryName]);
            return countryPhoneCodeMap[cleanCountryName];
        }

        for (const [country, code] of Object.entries(countryPhoneCodeMap)) {
            if (cleanCountryName.toLowerCase().includes(country.toLowerCase()) ||
                country.toLowerCase().includes(cleanCountryName.toLowerCase())) {
                console.log('Found partial match:', code, 'for', country);
                return code;
            }
        }

        console.log('No country code found for:', cleanCountryName);
        return '';
    };

    const createCountryOption = (countryName) => {
        const code = getCountryCode(countryName);
        if (code) {
            return {
                code: code,
                name: countryName,
                display: `${code} (${countryName})`
            };
        }
        return null;
    };

    const generateCountryOptions = (workCountry) => {
        let options = Object.entries(countryPhoneCodeMap).map(([country, code]) => ({
            code: code,
            name: country,
            display: `${code} (${country})`
        }));

        options.sort((a, b) => a.name.localeCompare(b.name));

        if (workCountry) {
            const workCountryOption = createCountryOption(workCountry);
            if (workCountryOption) {
                const existingIndex = options.findIndex(opt => opt.code === workCountryOption.code && opt.name === workCountryOption.name);
                if (existingIndex > -1) {
                    const [existing] = options.splice(existingIndex, 1);
                    options.unshift(existing);
                } else {
                    options.unshift(workCountryOption);
                }
            }
        }

        defaultCountryOptions.forEach(defaultOption => {
            const exists = options.some(opt => opt.code === defaultOption.code);
            if (!exists) {
                options.unshift(defaultOption);
            }
        });

        return options;
    };

    const autoFillCountryCodes = (country) => {
        const countryOption = createCountryOption(country);
        if (countryOption) {
            console.log('Auto-filling country codes with:', countryOption.display);

            setFormData(prev => ({
                ...prev,
                whatsapp_number_country_code: countryOption.display,
                phone_country_code: countryOption.display
            }));
        } else {
            console.log('Could not auto-fill country codes for:', country);
        }
    };

    const getDisplayValue = (storedValue, workCountry) => {
        if (!storedValue) return '';

        if (storedValue.includes('(') && storedValue.includes(')')) {
            return storedValue;
        }

        for (const [country, code] of Object.entries(countryPhoneCodeMap)) {
            if (code === storedValue) {
                return `${code} (${country})`;
            }
        }

        if (workCountry) {
            const workCountryCode = getCountryCode(workCountry);
            if (workCountryCode === storedValue) {
                return `${workCountryCode} (${workCountry})`;
            }
        }

        return storedValue;
    };

    const convertCurrency = (amount, fromCurrency, toCurrency) => {
        if (!amount || isNaN(amount) || !fromCurrency || !toCurrency) return '';

        const fromRate = conversionRates[fromCurrency] || 1;
        const toRate = conversionRates[toCurrency] || 1;

        const amountInUSD = parseFloat(amount) / fromRate;
        const convertedAmount = amountInUSD * toRate;

        const roundedAmount = Math.round(convertedAmount);
        return roundedAmount.toString();
    };

    const extractCurrencyCode = (currencyDisplay) => {
        if (!currencyDisplay) return '';
        const match = currencyDisplay.match(/^([A-Z]{3})/);
        return match ? match[1] : '';
    };

    const handleSalaryChange = (field, value) => {
        const currencyCode = extractCurrencyCode(formData.country_currency);

        // Remove non-numeric characters (allow only digits)
        const cleanValue = value.replace(/[^0-9]/g, '');

        setFormData(prev => {
            let updatedData = { ...prev, [field]: cleanValue };

            // Only calculate conversions if we have a clean value and currency
            if (cleanValue && currencyCode) {
                const numericValue = parseFloat(cleanValue);

                if (field === 'min_salary_local') {
                    updatedData.min_salary_usd = convertCurrency(numericValue, currencyCode, 'USD');
                    updatedData.min_salary_qar = convertCurrency(numericValue, currencyCode, 'QAR');
                } else if (field === 'min_salary_usd') {
                    updatedData.min_salary_local = convertCurrency(numericValue, 'USD', currencyCode);
                    updatedData.min_salary_qar = convertCurrency(numericValue, 'USD', 'QAR');
                } else if (field === 'min_salary_qar') {
                    updatedData.min_salary_local = convertCurrency(numericValue, 'QAR', currencyCode);
                    updatedData.min_salary_usd = convertCurrency(numericValue, 'QAR', 'USD');
                } else if (field === 'max_salary_local') {
                    updatedData.max_salary_usd = convertCurrency(numericValue, currencyCode, 'USD');
                    updatedData.max_salary_qar = convertCurrency(numericValue, currencyCode, 'QAR');
                } else if (field === 'max_salary_usd') {
                    updatedData.max_salary_local = convertCurrency(numericValue, 'USD', currencyCode);
                    updatedData.max_salary_qar = convertCurrency(numericValue, 'USD', 'QAR');
                } else if (field === 'max_salary_qar') {
                    updatedData.max_salary_local = convertCurrency(numericValue, 'QAR', currencyCode);
                    updatedData.max_salary_usd = convertCurrency(numericValue, 'QAR', 'USD');
                }
            } else if (!cleanValue) {
                // If the value is cleared, clear all related fields
                if (field.includes('min_')) {
                    updatedData.min_salary_local = '';
                    updatedData.min_salary_usd = '';
                    updatedData.min_salary_qar = '';
                } else if (field.includes('max_')) {
                    updatedData.max_salary_local = '';
                    updatedData.max_salary_usd = '';
                    updatedData.max_salary_qar = '';
                }
            }

            return updatedData;
        });

        // Validate salary fields
        setTimeout(() => {
            setErrors(validateField(field, cleanValue));
        }, 0);
    };

    const fetchSavedData = async () => {
        try {
            // Get user data from localStorage
            const userData = JSON.parse(localStorage.getItem('user'));

            // Fetch the agency profile data from registration to get agency_name, whatsapp, phone, etc.
            let agencyData = null;
            try {
                const agencyResponse = await fetch(`${BaseUrl}/api/agency/${userId}`);
                if (agencyResponse.ok) {
                    agencyData = await agencyResponse.json();
                    console.log('Agency profile data fetched:', agencyData);
                }
            } catch (agencyError) {
                console.error('Error fetching agency profile:', agencyError);
            }

            const response = await fetch(`${BaseUrl}/api/employer?temporary_id=${temporaryId}&user_id=${userId}`);
            if (response.ok) {
                const data = await response.json();

                if (data) {
                    const workCountry = data.country || '';
                    console.log('Work country from BasicInfo:', workCountry);
                    setWorkCountry(workCountry);

                    const storedCurrency = data.country_currency || 'QAR (Qatari Riyal)';
                    const currencyCode = extractCurrencyCode(storedCurrency);

                    // Check if selected category is a driver position
                    const driverPositions = [
                        'Home Driver', 'Company Driver', 'Light Driver', 'Motorbike Driver', 'Driver', 'Driver (Company / House)', 'Lady Driver',
                        'Bus Driver', 'Taxi Driver', 'Heavy Driver',
                        'Bike Driver',
                        'Delivery Drivers', 'Truck Drivers', 'Private Drivers',
                        'Delivery Van Driver', 'Heavy Truck Driver (CE)', 'Trailer Driver',
                        'Heavy Trailer Driver', 'Fuel Tanker Driver', 'Ready-Mix Driver'
                    ];

                    const hospitalJobPositions = [
                        "Home Nurse", "Home Care Nurse", "House Nurse", "Hospital Nurse", "Physiotherapy", "Nurse", "Clinic / Shop Nurse",
                        "School Nurse", "Company / Industrial Nurse", "Elderly Care Nurse",
                        "Special Care Nurse", "Emergency / Ambulance Nurse", "Lab / Assistant Nurse",
                        "Hotel / Resort Nurse", "Registered Nurse", "Healthcare Assistants",
                        "General Physician", "Specialist Doctor", "Consultant Doctor", "Surgeon",
                        "Pediatrician", "Gynecologist", "Orthopedic Doctor", "Cardiologist",
                        "Neurologist", "Dermatologist", "ENT Specialist", "Ophthalmologist (Eye Doctor)",
                        "Anesthesiologist", "Radiologist", "Psychiatrist", "Emergency Doctor",
                        "ICU Doctor", "Family Medicine Doctor", "Dental Doctor", "Medical Officer",
                        "Resident Doctor", "Doctors",
                        "Lab Technicians", "Physiotherapy", "Physiotherapists", "Elderly Care Workers", "Medical Technician"
                    ];

                    const companyWorkerPositions = [
                        "Workers", "General Worker", "Laborer", "Skilled Workers",
                        "Construction Workers", "Skilled Construction Workers", "Construction Worker",
                        "Mason", "Carpenter", "Steel Fixer", "Painter", "Scaffolder", "Plumber",
                        "AC Technician", "HVAC Technician", "Chiller Technician",
                        "Lathe & Milling Machine", "Lathe Machine Operator", "Milling Machine Operator",
                        "Heavy Equipment Operator", "Operator",
                        "Hospitality Workers", "Hotel Staff", "Waiters", "Waitresses", "Chefs", "Kitchen Helpers",
                        "Baristas", "Chef", "Kitchen Assistant", "Hotel Housekeeping", "Professional Chef",
                        "Kitchen Staff", "Dining Room Attendant", "Event Coordinator",
                        "Factory Workers", "Production Workers", "Production Worker", "Warehouse Workers",
                        "Warehouse Worker", "Packing Staff", "Picker & Packer",
                        "Machine Operators", "Machine Operator", "Assembly Line Workers", "Assembly Line Worker",
                        "Forklift Operators", "Forklift Operator",
                        "Logistics Staff", "Logistics Assistant",
                        "Farm Workers", "Farmar", "Fruit Pickers", "Dairy Workers", "Poultry Workers", "Greenhouse Workers",
                        "Caretakers",
                        "CNC Operator", "CNC Programmer",
                        "MIG Welder", "TIG Welder", "Industrial Welder", "Fabricator", "Pipe Fitter",
                        "Steel Structure Welder", "6G Welder", "Structural Welder", "Welder",
                        "Heavy Truck Mechanic", "Diesel Mechanic", "Auto Mechanic", "Bus Mechanic",
                        "Vehicle Technician", "Heavy Equipment Mechanic", "Mechanic", "Mechanical Maintenance Technician",
                        "Industrial Electrician", "Maintenance Electrician", "Panel Electrician", "Maintenance Technician",
                        "Electrical Technician", "PLC Technician", "Automation Technician", "Electrician", "Instrument Technician",
                        "Company Driver", "Light Driver", "Heavy Driver", "Delivery Drivers", "Truck Drivers", "Delivery Van Driver",
                        "Heavy Truck Driver (CE)", "Trailer Driver", "Heavy Trailer Driver", "Fuel Tanker Driver",
                        "Ready-Mix Driver", "Driver", "Driver (Company / House)", "Taxi Driver", "Bus Driver", "Motorbike Driver", "Chauffeur",
                        "Bike Driver", "JCB Operator", "Bulldozer Operator", "Excavator Operator", "Loader Operator", "Skid Steer Loader Operator",
                        "Grader Operator", "Trench Excavator / Trencher Operator", "Backhoe Operator", "Dragline Operator", "Scraper Operator",
                        "Crane Operator", "Telehandler Operator", "Reach Stacker Operator", "Boom Lift / Cherry Picker Operator",
                        "Container Handler Operator", "Road Roller Operator", "Asphalt Paver Operator", "Bitumen Sprayer Operator",
                        "Concrete Paver Operator", "Soil Stabilizer Operator", "Drilling Machine / Drill Rig Operator",
                        "Rock Breaker / Hydraulic Hammer Operator", "Loader-Dumper Operator", "Dump Truck Operator",
                        "Stone Crusher Operator", "Grizzly Feeder / Conveyor Operator", "Tractor Operator", "Harvester / Combine Operator",
                        "Dozer / Land Leveling Operator", "Tower Crane Operator", "Mobile Crane Operator", "Crawler Crane Operator",
                        "Piling Machine Operator", "Concrete Mixer Truck Operator", "Concrete Pump Operator", "Compactor Operator",
                        "Pipe Layer Operator", "Forklift / Warehouse Equipment Operator", "Boom Truck / Loader Crane Operator",
                        "Road Sweeper Operator", "Water Tanker / Firefighting Vehicle Operator", "Excavator Mounted Drill Operator",
                        "Heavy Truck / Tip Truck Operator", "Loader Excavator Operator", "Teleporter Crane Operator",
                        "Material Handler / Port Crane Operator", "Hydraulic Excavator Operator", "Multi-Functional Heavy M"
                    ];

                    const isDriverCategory = driverPositions.includes(data.domestic_worker_category);

                    // Auto-fill salary for drivers if not already saved
                    let minSalaryLocal = data.minimum_monthly_salary || '';
                    let maxSalaryLocal = data.maximum_monthly_salary || '';

                    if (isDriverCategory && !minSalaryLocal && !maxSalaryLocal) {
                        // Default driver salary in QAR
                        minSalaryLocal = '1700';
                        maxSalaryLocal = '2000';
                        console.log('[Auto-Fill] Driver category detected - setting salary to 1700-2000 QAR');
                    }

                    const minSalaryUSD = minSalaryLocal ? convertCurrency(minSalaryLocal, currencyCode, 'USD') : '';
                    const minSalaryQAR = minSalaryLocal ? convertCurrency(minSalaryLocal, currencyCode, 'QAR') : '';
                    const maxSalaryUSD = maxSalaryLocal ? convertCurrency(maxSalaryLocal, currencyCode, 'USD') : '';
                    const maxSalaryQAR = maxSalaryLocal ? convertCurrency(maxSalaryLocal, currencyCode, 'QAR') : '';

                    // Pre-fill from employer data first, then agency profile, then localStorage
                    const userName = data.name || agencyData?.owner_name || (userData ? `${userData.first_name || ''} ${userData.last_name || ''}`.trim() : '');
                    const userOrg = data.organization_name || agencyData?.agency_name || '';
                    const userEmail = data.email_id || agencyData?.email || userData?.email || '';
                    const userWhatsApp = data.whatsapp_number || agencyData?.whatsapp_number_1 || '';
                    const whatsappCode = data.whatsapp_number_country_code || agencyData?.whatsapp_number_1_country_code || '';

                    const userPhone = data.phone_number || agencyData?.official_phone || '';
                    const phoneCode = data.phone_country_code || agencyData?.official_phone_country_code || '';

                    const whatsappDisplay = getDisplayValue(whatsappCode, workCountry);
                    const phoneDisplay = getDisplayValue(phoneCode, workCountry);

                    const parsedOffers = parseArray(data.offer_for_selected_candidates);
                    const defaultOffers = ["Free food", "Accommodation", "Transportation", "Free Medical", "Yearly holiday", "Paid Leave", "Weekly off", "2 years contract"];

                    let defaultLookingWorkerFor = 'For family';
                    if (data.domestic_worker_category) {
                        const isHospital = hospitalJobPositions.includes(data.domestic_worker_category);
                        const isCompanyWorker = companyWorkerPositions.includes(data.domestic_worker_category);

                        if (isHospital || isCompanyWorker) {
                            defaultLookingWorkerFor = 'For company';
                        }
                    }

                    const updatedFormData = {
                        looking_worker_for: data.looking_worker_for || defaultLookingWorkerFor,
                        name: userName,
                        nationality: data.nationality || '',
                        organization_name: userOrg,
                        adults: data.adults || 0,
                        children: data.children || 0,
                        type_of_house: data.type_of_house || '',
                        rooms: data.rooms || 0,
                        bathrooms: data.bathrooms || 0,
                        have_domestic_worker: data.have_domestic_worker || false,
                        worker_nationality: data.worker_nationality || '',
                        have_pets: data.have_pets || false,
                        email_id: userEmail,
                        contact_source: data.contact_source || '',
                        whatsapp_number_country_code: whatsappDisplay,
                        whatsapp_number: userWhatsApp,
                        phone_country_code: phoneDisplay,
                        phone_number: userPhone,
                        offer_for_selected_candidates: parsedOffers.length > 0 ? parsedOffers : defaultOffers,
                        country_currency: storedCurrency,
                        minimum_monthly_salary: minSalaryLocal,
                        maximum_monthly_salary: maxSalaryLocal,
                        negotiable: data.negotiable || false,
                        min_salary_local: minSalaryLocal,
                        max_salary_local: maxSalaryLocal,
                        min_salary_usd: minSalaryUSD,
                        max_salary_usd: maxSalaryUSD,
                        min_salary_qar: minSalaryQAR,
                        max_salary_qar: maxSalaryQAR
                    };

                    setFormData(updatedFormData);

                    // If country codes are not set, try to auto-fill based on nationality
                    if (!whatsappDisplay && !phoneDisplay && data.nationality) {
                        console.log('Auto-filling country codes based on nationality:', data.nationality);
                        autoFillCountryCodes(data.nationality);
                    }
                } else {
                    // If no employer data exists, pre-fill from agency profile
                    if (agencyData) {
                        const userName = agencyData.owner_name || '';
                        const userOrg = agencyData.agency_name || '';
                        const userEmail = agencyData.email || '';
                        const userWhatsApp = agencyData.whatsapp_number_1 || '';
                        const whatsappCode = agencyData.whatsapp_number_1_country_code || '';
                        const userPhone = agencyData.official_phone || '';
                        const phoneCode = agencyData.official_phone_country_code || '';

                        const whatsappDisplay = getDisplayValue(whatsappCode, '');
                        const phoneDisplay = getDisplayValue(phoneCode, '');

                        setFormData(prev => ({
                            ...prev,
                            name: userName,
                            organization_name: userOrg,
                            email_id: userEmail,
                            whatsapp_number: userWhatsApp,
                            whatsapp_number_country_code: whatsappDisplay,
                            phone_number: userPhone,
                            phone_country_code: phoneDisplay
                        }));
                    } else if (userData) {
                        const userName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
                        setFormData(prev => ({
                            ...prev,
                            name: userName,
                            email_id: userData.email || '',
                            whatsapp_number: userData.whatsapp_number || '',
                            phone_number: userData.mobile_number || ''
                        }));
                    }
                }
            } else {
                console.error('Failed to fetch data:', response.status);
                // Pre-fill from agency profile even if employer fetch fails
                if (agencyData) {
                    setFormData(prev => ({
                        ...prev,
                        name: agencyData.owner_name || '',
                        organization_name: agencyData.agency_name || '',
                        email_id: agencyData.email || '',
                        whatsapp_number: agencyData.whatsapp_number_1 || '',
                        whatsapp_number_country_code: getDisplayValue(agencyData.whatsapp_number_1_country_code || '', ''),
                        phone_number: agencyData.official_phone || '',
                        phone_country_code: getDisplayValue(agencyData.official_phone_country_code || '', '')
                    }));
                } else if (userData) {
                    const userName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
                    setFormData(prev => ({
                        ...prev,
                        name: userName,
                        whatsapp_number: userData.whatsapp_number || '',
                        phone_number: userData.phone_number || ''
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching employer data:', error);
            // Pre-fill from user registration on error
            const userData = JSON.parse(localStorage.getItem('user'));
            if (userData) {
                const userName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
                setFormData(prev => ({
                    ...prev,
                    name: userName,
                    whatsapp_number: userData.whatsapp_number || '',
                    phone_number: userData.phone_number || ''
                }));
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) {
            fetchSavedData();
        }
    }, [userId, temporaryId]);

    const handleSelect = (field, value) => {
        if (field === 'offer_for_selected_candidates') {
            setFormData(prev => {
                if (prev.offer_for_selected_candidates.includes(value)) {
                    return {
                        ...prev,
                        [field]: prev.offer_for_selected_candidates.filter(item => item !== value)
                    };
                } else {
                    return {
                        ...prev,
                        [field]: [...prev.offer_for_selected_candidates, value]
                    };
                }
            });
        } else if (field === 'have_domestic_worker' || field === 'have_pets') {
            setFormData(prev => ({
                ...prev,
                [field]: value === 'Yes'
            }));
        } else if (field === 'looking_worker_for') {
            setFormData(prev => ({
                ...prev,
                [field]: value,
                ...(value === 'For company' ? {
                    adults: 0,
                    children: 0,
                    type_of_house: '',
                    rooms: 0,
                    bathrooms: 0,
                    have_domestic_worker: false,
                    worker_nationality: '',
                    have_pets: false
                } : {})
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));

            // If nationality is selected, auto-fill country codes
            if (field === 'nationality') {
                const countryName = value;
                const countryOption = createCountryOption(countryName);

                if (countryOption) {
                    console.log('Auto-filling country codes for nationality:', countryName);

                    setFormData(prev => ({
                        ...prev,
                        whatsapp_number_country_code: countryOption.display,
                        phone_country_code: countryOption.display
                    }));
                }
            }
        }

        setTimeout(() => {
            setErrors(validateField(field, value));
        }, 0);
    };

    const isSelected = (field, value) => {
        if (field === 'have_domestic_worker' || field === 'have_pets') {
            return formData[field] === (value === 'Yes');
        }
        if (field === 'offer_for_selected_candidates') {
            return formData.offer_for_selected_candidates.includes(value);
        }
        if (Array.isArray(formData[field])) {
            return formData[field].includes(value);
        }
        return formData[field] === value;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Check if this is a salary field
        if (name.includes('_salary_')) {
            // Allow only digits in salary fields
            const onlyNums = value.replace(/[^0-9]/g, '');
            handleSalaryChange(name, onlyNums);
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        // Validate on change for certain fields
        if (['email_id', 'whatsapp_number', 'phone_number', 'min_salary_local', 'max_salary_local'].includes(name)) {
            setTimeout(() => {
                setErrors(validateField(name, value));
            }, 0);
        }
    };

    const handleNumberChange = (field, increment) => {
        const newValue = Math.max(0, formData[field] + (increment ? 1 : -1));
        setFormData(prev => ({
            ...prev,
            [field]: newValue
        }));

        setTimeout(() => {
            setErrors(validateField(field, newValue));
        }, 0);
    };

    const handleSubmit = async (action) => {
        if (!userId || !temporaryId) {
            Swal.fire('Error', 'User not authenticated or missing temporary ID', 'error');
            return;
        }

        // Validate form before submission
        if (!validateForm()) {
            Swal.fire('Validation Error', 'Please fix the errors in the form before submitting.', 'error');
            focusFirstError();
            return;
        }

        try {
            const extractCode = (displayValue) => {
                if (!displayValue) return '';
                const match = displayValue.match(/^(\+\d+)/);
                return match ? match[1] : displayValue;
            };

            const whatsappCode = extractCode(formData.whatsapp_number_country_code);
            const phoneCode = extractCode(formData.phone_country_code);

            const payload = {
                temporary_id: temporaryId,
                user_id: userId,
                looking_worker_for: formData.looking_worker_for,
                name: formData.name,
                nationality: formData.nationality,
                organization_name: formData.organization_name,
                adults: formData.adults,
                children: formData.children,
                type_of_house: formData.type_of_house,
                rooms: formData.rooms,
                bathrooms: formData.bathrooms,
                have_domestic_worker: formData.have_domestic_worker,
                worker_nationality: formData.worker_nationality,
                have_pets: formData.have_pets,
                email_id: formData.email_id,
                contact_source: formData.contact_source,
                whatsapp_number_country_code: whatsappCode,
                whatsapp_number: formData.whatsapp_number,
                phone_country_code: phoneCode,
                phone_number: formData.phone_number,
                offer_for_selected_candidates: JSON.stringify(formData.offer_for_selected_candidates),
                country_currency: formData.country_currency,
                minimum_monthly_salary: formData.min_salary_local,
                maximum_monthly_salary: formData.max_salary_local,
                negotiable: formData.negotiable,
                min_salary_local: formData.min_salary_local,
                max_salary_local: formData.max_salary_local,
                min_salary_usd: formData.min_salary_usd,
                max_salary_usd: formData.max_salary_usd,
                min_salary_qar: formData.min_salary_qar,
                max_salary_qar: formData.max_salary_qar
            };

            const response = await fetch(`${BaseUrl}/api/employer`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to save data');
            }

            Swal.fire('Success', 'Data saved successfully', 'success');

            if (action === 'next') {
                window.scrollTo(0, 0);
                navigate('/a-jobdescription', { state: { userId, temporaryId } });
            } else if (action === 'back') {
                window.scrollTo(0, 0);
                navigate('/a-candidatepreference', { state: { userId, temporaryId } });
            }
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.message || 'Failed to save data', 'error');
        }
    };

    const handleBack = () => {
        navigate('/a-candidatepreference', { state: { userId, temporaryId } });
    };

    const selectedButtonStyle = {
        backgroundColor: '#0d47a1',
        color: 'white',
        borderColor: '#0d47a1'
    };

    const decrementButtonStyle = {
        backgroundColor: 'rgb(23 5 227 / 51%)',
        color: 'white',
        borderColor: 'rgb(23 5 227 / 51%)',
        width: '45px',
        height: '45px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        fontWeight: 'bold',
        fontSize: '20px',
        marginTop: '0px'
    };

    const incrementButtonStyle = {
        backgroundColor: 'rgb(23 5 227 / 51%)',
        color: 'white',
        borderColor: 'rgb(23 5 227 / 51%)',
        width: '45px',
        height: '45px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        fontWeight: 'bold',
        fontSize: '20px',
        marginTop: '0px'
    };

    const disabledButtonStyle = {
        backgroundColor: '#cccccc',
        color: '#666666',
        borderColor: '#cccccc',
        width: '45px',
        height: '45px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        fontWeight: 'bold',
        fontSize: '20px',
        marginTop: '0px'
    };

    const countDisplayStyle = {
        fontSize: '24px',
        fontWeight: 'bold',
        minWidth: '40px',
        textAlign: 'center',
        padding: '5px 10px',
        backgroundColor: '#f8f9fa',
        borderRadius: '0px',
        border: '2px solid #dee2e6'
    };

    const CustomDropdown = ({
        title,
        field,
        options,
        selectedValue,
        onSelect,
        required = false,
        error = null
    }) => {
        const [localSearchTerm, setLocalSearchTerm] = useState('');
        const [isOpen, setIsOpen] = useState(false);

        const displayOptions = options.map(opt => opt.display || opt);
        const filteredOptions = displayOptions.filter(option =>
            option.toLowerCase().includes(localSearchTerm.toLowerCase())
        );

        return (
            <div className="mb-3">
                <Dropdown
                    show={isOpen}
                    onToggle={(isOpen) => setIsOpen(isOpen)}
                >
                    <Dropdown.Toggle
                        variant="light"
                        className={`language-select w-100 d-flex justify-content-between align-items-center text-center ${error ? 'is-invalid' : ''}`}
                        id={`dropdown-${field}`}
                        style={{
                            paddingLeft: '12px',
                            paddingRight: '12px'
                        }}
                    >
                        <span className="w-100 text-center">{selectedValue || `${title}${required ? ' *' : ''}`}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                        <div className="px-2 py-1">
                            <Form.Control
                                type="text"
                                placeholder={`Search ${title.toLowerCase()}...`}
                                value={localSearchTerm}
                                onChange={(e) => setLocalSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                className="mb-2"
                                autoFocus
                            />
                        </div>
                        <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option) => (
                                    <Dropdown.Item
                                        key={option}
                                        active={selectedValue === option}
                                        onClick={() => {
                                            onSelect(field, option);
                                            setLocalSearchTerm('');
                                            setIsOpen(false);
                                        }}
                                        className="text-center"
                                    >
                                        {option}
                                    </Dropdown.Item>
                                ))
                            ) : (
                                <Dropdown.Item disabled className="text-center">No matches found</Dropdown.Item>
                            )}
                        </div>
                    </Dropdown.Menu>
                </Dropdown>
                {error && <div className="invalid-feedback d-block">{error}</div>}
            </div>
        );
    };

    // Generate country options based on work country
    const countryOptions = generateCountryOptions(workCountry);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            <AgencyNavbar />
            <Container fluid className="job-form py-4" style={{ marginTop: '70px' }} ref={formRef}>
                <div className="form-header text-center">
                    <h1 className="d-flex align-items-center justify-content-center">
                        <i className="bi bi-person-vcard me-2" style={{ fontSize: '60px' }}></i>
                        About yourself
                    </h1>
                </div>

                {/* Display validation errors summary */}
                {Object.keys(errors).length > 0 && (
                    <Alert variant="danger" className="mb-4">
                        <strong>Please fix the following errors:</strong>
                        <ul className="mb-0 mt-2">
                            {Object.entries(errors).map(([field, error]) => (
                                <li key={field}>{error}</li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {/* Looking for a worker */}
                <div className="section mt-3">
                    <h4 className="d-flex align-items-center about-yourself">
                        <span style={{ color: 'black' }}>Looking for a worker</span>
                    </h4>
                    {errors.looking_worker_for && <div className="text-danger mb-2">{errors.looking_worker_for}</div>}
                    <Row className="g-3 mb-3">
                        {["For family", "For company"].map((option, idx) => (
                            <Col xs={4} md={6} key={idx}>
                                <Button
                                    className={`language-btn w-100 ${isSelected('looking_worker_for', option) ? 'selected-button' : ''}`}
                                    variant="outline-primary"
                                    onClick={() => handleSelect('looking_worker_for', option)}
                                >
                                    {option}
                                </Button>
                            </Col>
                        ))}
                    </Row>
                </div>

                {/* What is your name */}
                <div className="section mt-3">
                    <h4 className="d-flex align-items-center about-yourself">
                        <span style={{ color: 'black' }}>Contact person name ?
                            <span className="text-danger">*</span>
                        </span>
                    </h4>
                    <div className="dropdown-section mt-3">
                        <Form.Control
                            ref={nameRef}
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            onBlur={() => handleBlur('name')}
                            placeholder="Type your name here"
                            className={`w-100 ${errors.name ? 'is-invalid' : ''}`}
                            isInvalid={!!errors.name}
                        />
                        {errors.name && <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>}
                    </div>
                </div>

                {/* What is your nationality */}
                <div className="section mt-3 nationality-dropdown-container">
                    <h4 className="d-flex align-items-center about-yourself">
                        <span style={{ color: 'black' }}>Nationality?
                            <span className="text-danger">*</span>
                        </span>
                    </h4>
                    <div className="dropdown-section mt-3">
                        <CustomDropdown
                            title="Select your nationality here"
                            field="nationality"
                            options={nationalityOptions}
                            selectedValue={formData.nationality}
                            onSelect={handleSelect}
                            error={errors.nationality}
                            required
                        />
                    </div>
                </div>

                {/* Organization name (Optional) */}
                <div className="section mt-3">
                    <h4 className="d-flex align-items-center about-yourself">
                        <span style={{ color: 'black' }}>Organization name?</span>
                    </h4>
                    <div className="dropdown-section mt-3">
                        <Form.Control
                            type="text"
                            name="organization_name"
                            value={formData.organization_name}
                            onChange={handleInputChange}
                            placeholder="Type your Organization name here"
                            className="w-100"
                        />
                    </div>
                </div>

                {/* Family-only section */}
                {!isCompanyFlow && (
                <>
                <div className="section mt-3">
                    <h4 className="d-flex align-items-center about-yourself mb-3">
                        <span style={{ color: 'black' }}>How many people are in client family?</span>
                    </h4>
                    {errors.adults && <div className="text-danger mb-2">{errors.adults}</div>}
                    {errors.children && <div className="text-danger mb-2">{errors.children}</div>}
                    <Row className="g-3 mb-3">
                        {[
                            { label: "Adults", key: "adults" },
                            { label: "Children", key: "children" }
                        ].map((item, idx) => (
                            <React.Fragment key={idx}>
                                <Col xs={6}>
                                    <div className="label-box d-flex align-items-center justify-content-center">
                                        {item.label}
                                    </div>
                                </Col>

                                <Col xs={6}>
                                    <div className="button-box d-flex align-items-center justify-content-between px-4">
                                        <Button
                                            variant="light"
                                            className="custom-button  d-flex align-items-center justify-content-center"
                                            disabled={formData[item.key] <= 0}
                                            onClick={() => handleNumberChange(item.key, false)}
                                            style={formData[item.key] <= 0 ? disabledButtonStyle : decrementButtonStyle}
                                            size="sm"
                                        >
                                            <BsDash />
                                        </Button>

                                        <div
                                            className={`count-display mx-2 ${errors[item.key] ? 'border-danger' : ''}`}
                                            style={countDisplayStyle}
                                        >
                                            {formData[item.key]}
                                        </div>

                                        <Button
                                            variant="light"
                                            className="custom-button  d-flex align-items-center justify-content-center"
                                            onClick={() => handleNumberChange(item.key, true)}
                                            style={incrementButtonStyle}
                                            size="sm"
                                        >
                                            <BsPlus />
                                        </Button>
                                    </div>
                                </Col>
                            </React.Fragment>
                        ))}
                    </Row>
                </div>

                {/* Type of House */}
                <div className="section mt-3">
                    <h4 className="d-flex align-items-center about-yourself">
                        <span style={{ color: 'black' }}>Type of House</span>
                    </h4>

                    {errors.type_of_house && <div className="text-danger mb-2">{errors.type_of_house}</div>}
                    <Row className="g-3 mb-4">
                        {["Apartment", "Villa", "Palace"].map((option, idx) => (
                            <Col xs={4} md={4} key={idx}>
                                <Button
                                    className={`language-btn w-100 ${isSelected('type_of_house', option) ? 'selected-button' : ''}`}
                                    variant="outline-primary"
                                    onClick={() => handleSelect('type_of_house', option)}
                                >
                                    {option}
                                </Button>
                            </Col>
                        ))}
                    </Row>

                    {/* Rooms and Bathrooms Section */}
                    <Row className="g-3 mb-3 align-items-center">
                        {/* Rooms Section */}
                        <Col xs={12} sm={6} className="mb-sm-0">
                            <Row className="align-items-center">
                                <Col xs={4} sm={4}>
                                    <div className="label-box d-flex align-items-center justify-content-center h-100 about-yourself">
                                        <span>Rooms</span>
                                    </div>
                                </Col>
                                <Col xs={8} sm={8}>
                                    <div className="button-box d-flex align-items-center justify-content-center rooms">
                                        <Button
                                            variant="light"
                                            className="custom-button  d-flex align-items-center justify-content-center"
                                            disabled={formData.rooms <= 0}
                                            onClick={() => handleNumberChange('rooms', false)}
                                            style={formData.rooms <= 0 ? disabledButtonStyle : decrementButtonStyle}
                                            size="sm"
                                        >
                                            <BsDash />
                                        </Button>
                                        <div
                                            className={`count-display mx-3 ${errors.rooms ? 'border-danger' : ''}`}
                                            style={countDisplayStyle}
                                        >
                                            {formData.rooms}
                                        </div>
                                        <Button
                                            variant="light"
                                            className="custom-button d-flex align-items-center justify-content-center"
                                            onClick={() => handleNumberChange('rooms', true)}
                                            style={incrementButtonStyle}
                                            size="sm"
                                        >
                                            <BsPlus />
                                        </Button>
                                    </div>
                                    {errors.rooms && <div className="text-danger mt-1 small">{errors.rooms}</div>}
                                </Col>
                            </Row>
                        </Col>

                        {/* Bathrooms Section */}
                        <Col xs={12} sm={6}>
                            <Row className="align-items-center">
                                <Col xs={4} sm={4}>
                                    <div className="label-box d-flex align-items-center justify-content-center h-100 about-yourself">
                                        <span>Bathroom</span>
                                    </div>
                                </Col>
                                <Col xs={8} sm={8}>
                                    <div className="button-box d-flex align-items-center justify-content-center bathrooms">
                                        <Button
                                            variant="light"
                                            className="custom-button rounded-circle d-flex align-items-center justify-content-center"
                                            disabled={formData.bathrooms <= 0}
                                            onClick={() => handleNumberChange('bathrooms', false)}
                                            style={formData.bathrooms <= 0 ? disabledButtonStyle : decrementButtonStyle}
                                            size="sm"
                                        >
                                            <BsDash />
                                        </Button>
                                        <div
                                            className={`count-display mx-3 ${errors.bathrooms ? 'border-danger' : ''}`}
                                            style={countDisplayStyle}
                                        >
                                            {formData.bathrooms}
                                        </div>
                                        <Button
                                            variant="light"
                                            className="custom-button rounded-circle d-flex align-items-center justify-content-center"
                                            onClick={() => handleNumberChange('bathrooms', true)}
                                            style={incrementButtonStyle}
                                            size="sm"
                                        >
                                            <BsPlus />
                                        </Button>
                                    </div>
                                    {errors.bathrooms && <div className="text-danger mt-1 small">{errors.bathrooms}</div>}
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    <h4 className="d-flex align-items-center about-yourself">
                        <span style={{ color: 'black', marginTop: '10px' }} > Do you have a domestic worker?</span>
                    </h4>
                    <Row className="g-3 mb-3">
                        {["Yes", "No"].map((option, idx) => (
                            <Col xs={12} md={6} key={idx}>
                                <Button
                                    className={`language-btn w-100 ${isSelected('have_domestic_worker', option) ? 'selected-button' : ''}`}
                                    variant="outline-primary"
                                    onClick={() => handleSelect('have_domestic_worker', option)}
                                >
                                    {option}
                                </Button>
                            </Col>
                        ))}
                    </Row>
                    {formData.have_domestic_worker && (
                        <div className="dropdown-section mt-3">
                            <CustomDropdown
                                title="What is the nationality of your worker"
                                field="worker_nationality"
                                options={workerNationalityOptions}
                                selectedValue={formData.worker_nationality}
                                onSelect={handleSelect}
                                error={errors.worker_nationality}
                                required
                            />
                        </div>
                    )}

                    <h4 className="d-flex align-items-center about-yourself" style={{ marginTop: '10px' }}>
                        <span style={{ color: 'black', marginTop: '10px' }}> Do you have any pets?</span>
                    </h4>
                    <Row className="g-3 mb-3">
                        {["Yes", "No"].map((option, idx) => (
                            <Col xs={12} md={6} key={idx}>
                                <Button
                                    className={`language-btn w-100 ${isSelected('have_pets', option) ? 'selected-button' : ''}`}
                                    variant="outline-primary"
                                    onClick={() => handleSelect('have_pets', option)}
                                >
                                    {option}
                                </Button>
                            </Col>
                        ))}
                    </Row>
                </div>
                </>
                )}

                    <h4 style={{ marginTop: '10px', textAlign: 'left', marginBottom: '0px' }} className="about-yourself"> Email
                        <span className="text-danger">*</span>
                    </h4>

                    {/* Email Input */}
                    <div className="dropdown-section mt-3" style={{ marginBottom: '25px' }}>
                        <Form.Control
                            ref={emailRef}
                            type="email"
                            name="email_id"
                            value={formData.email_id}
                            onChange={handleInputChange}
                            onBlur={() => handleBlur('email_id')}
                            placeholder="Type your Email Id"
                            className={`w-100 ${errors.email_id ? 'is-invalid' : ''}`}
                            isInvalid={!!errors.email_id}
                        />
                        {errors.email_id && <Form.Control.Feedback type="invalid">{errors.email_id}</Form.Control.Feedback>}
                    </div>

                    <h4 style={{ marginTop: '30px', textAlign: 'left', marginBottom: '0px' }} className="about-yourself">  WhatsApp
                        <span className="text-danger">*</span>
                    </h4>
                    <Row className="mb-2 g-2">
                        <Col xs={6} md={6} lg={6}>
                            <CustomDropdown
                                title="Country code"
                                field="whatsapp_number_country_code"
                                options={countryOptions}
                                selectedValue={formData.whatsapp_number_country_code}
                                onSelect={handleSelect}
                                error={errors.whatsapp_number_country_code}
                            />
                        </Col>
                        <Col xs={6} md={6} lg={6}>
                            <Form.Control
                                ref={whatsappRef}
                                type="tel"
                                name="whatsapp_number"
                                value={formData.whatsapp_number}
                                onChange={handleInputChange}
                                onBlur={() => handleBlur('whatsapp_number')}
                                placeholder="Type WhatsApp number here"
                                className={`contact-input-aboutyourself ${errors.whatsapp_number ? 'is-invalid' : ''}`}
                                isInvalid={!!errors.whatsapp_number}
                            />
                            {errors.whatsapp_number && <Form.Control.Feedback type="invalid">{errors.whatsapp_number}</Form.Control.Feedback>}
                        </Col>
                    </Row>

                    <h4 style={{ marginTop: '10px', textAlign: 'left', marginBottom: '0px' }} className="about-yourself">   Mobile Phone
                        <span className="text-danger">*</span>
                    </h4>
                    <Row className="mb-2 g-2" style={{ marginTop: '-20px' }}>
                        <Col xs={6} md={6} lg={6}>
                            <CustomDropdown
                                title="Country code "
                                field="phone_country_code"
                                options={countryOptions}
                                selectedValue={formData.phone_country_code}
                                onSelect={handleSelect}
                                error={errors.phone_country_code}
                            />
                        </Col>
                        <Col xs={6} md={6} lg={6}>
                            <Form.Control
                                ref={phoneRef}
                                type="tel"
                                name="phone_number"
                                value={formData.phone_number}
                                onChange={handleInputChange}
                                onBlur={() => handleBlur('phone_number')}
                                placeholder="Type phone number here"
                                className={`contact-input-aboutyourself ${errors.phone_number ? 'is-invalid' : ''}`}
                                isInvalid={!!errors.phone_number}
                            />
                            {errors.phone_number && <Form.Control.Feedback type="invalid">{errors.phone_number}</Form.Control.Feedback>}
                        </Col>
                    </Row>

                    <h4 className="d-flex align-items-center about-yourself">
                        <span style={{ color: 'black', marginTop: '0px' }}>  What do you offer for selected candidates?</span>
                    </h4>

                    <Row className="g-3 mb-3">
                        {["Free food", "Accommodation", "Transportation", "Free Medical", "Yearly holiday", "Paid Leave", "Weekly off", "2 years contract"].map((option, idx) => (
                            <Col xs={6} md={3} key={idx}>
                                <Button
                                    className={`language-btn w-100 ${isSelected('offer_for_selected_candidates', option) ? 'selected-button' : ''}`}
                                    variant="outline-primary"
                                    onClick={() => handleSelect('offer_for_selected_candidates', option)}
                                    style={isSelected('offer_for_selected_candidates', option) ? selectedButtonStyle : {}}
                                >
                                    {option}
                                    {isSelected('offer_for_selected_candidates', option) && (
                                        <span className="ms-2">
                                            <FaCheckCircle />
                                        </span>
                                    )}
                                </Button>
                            </Col>
                        ))}
                    </Row>

                    <h4 className="d-flex align-items-center about-yourself">
                        <span style={{ color: 'black', marginTop: '10px' }}>
                            Monthly Salary Offer?*
                            <span className="" style={{ fontSize: '14px', color: 'black', marginTop: '0px' }}> (Salary as per labour law)</span>
                        </span>
                    </h4>

                    <div className="dropdown-section mt-3">
                        <CustomDropdown
                            title="Select Your Country Currency Here"
                            field="country_currency"
                            options={currencyOptions}
                            selectedValue={formData.country_currency}
                            onSelect={handleSelect}
                            error={errors.country_currency}
                            required
                        />
                    </div>

                    {/* Minimum Salary Section */}
                    <div className="salary-section mt-3">
                        <Row className="g-3 mb-4">
                            <Col xs={12} md={4}>
                                <Form.Control
                                    type="text"
                                    inputMode="numeric"
                                    name="min_salary_local"
                                    value={formData.min_salary_local}
                                    onChange={handleInputChange}
                                    onBlur={() => handleBlur('min_salary_local')}
                                    placeholder={`Minimum Salary ${formData.country_currency ? formData.country_currency.split(' ')[0] : ""}`}
                                    className={`contact-input ${errors.min_salary_local ? 'is-invalid' : ''}`}
                                    isInvalid={!!errors.min_salary_local}
                                />
                                {errors.min_salary_local && (
                                    <Form.Control.Feedback type="invalid">
                                        {errors.min_salary_local}
                                    </Form.Control.Feedback>
                                )}
                            </Col>

                            <Col xs={6} md={4}>
                                <div className="input-group salary-input-group">
                                    <span className="input-group-text">USD</span>
                                    <Form.Control
                                        type="text"
                                        inputMode="numeric"
                                        name="min_salary_usd"
                                        value={formData.min_salary_usd}
                                        onChange={handleInputChange}
                                        placeholder="Minimum Salary in USD"
                                        className={`contact-input ${errors.min_salary_local ? 'is-invalid' : ''}`}
                                    />
                                </div>
                            </Col>

                            <Col xs={6} md={4}>
                                <div className="input-group salary-input-group">
                                    <span className="input-group-text">QAR</span>
                                    <Form.Control
                                        type="text"
                                        inputMode="numeric"
                                        name="min_salary_qar"
                                        value={formData.min_salary_qar}
                                        onChange={handleInputChange}
                                        placeholder="Minimum Salary in QAR"
                                        className={`contact-input ${errors.min_salary_local ? 'is-invalid' : ''}`}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* Maximum Salary Section */}
                    <div className="salary-section mt-3">
                        <Row className="g-3 mb-2">
                            <Col xs={12} md={4}>
                                <Form.Control
                                    type="text"
                                    inputMode="numeric"
                                    name="max_salary_local"
                                    value={formData.max_salary_local}
                                    onChange={handleInputChange}
                                    onBlur={() => handleBlur('max_salary_local')}
                                    placeholder={`Maximum Salary ${formData.country_currency ? formData.country_currency.split(' ')[0] : ""}`}
                                    className={`contact-input ${errors.max_salary_local ? 'is-invalid' : ''}`}
                                    isInvalid={!!errors.max_salary_local}
                                />
                                {errors.max_salary_local && (
                                    <Form.Control.Feedback type="invalid">
                                        {errors.max_salary_local}
                                    </Form.Control.Feedback>
                                )}
                            </Col>

                            <Col xs={6} md={4}>
                                <div className="input-group salary-input-group">
                                    <span className="input-group-text">USD</span>
                                    <Form.Control
                                        type="text"
                                        inputMode="numeric"
                                        name="max_salary_usd"
                                        value={formData.max_salary_usd}
                                        onChange={handleInputChange}
                                        placeholder="Maximum Salary in USD"
                                        className={`contact-input ${errors.max_salary_local ? 'is-invalid' : ''}`}
                                    />
                                </div>
                            </Col>

                            <Col xs={6} md={4}>
                                <div className="input-group salary-input-group">
                                    <span className="input-group-text">QAR</span>
                                    <Form.Control
                                        type="text"
                                        inputMode="numeric"
                                        name="max_salary_qar"
                                        value={formData.max_salary_qar}
                                        onChange={handleInputChange}
                                        placeholder="Maximum Salary in QAR"
                                        className={`contact-input ${errors.max_salary_local ? 'is-invalid' : ''}`}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* Negotiable Button */}
                    <Row className="g-3 mb-1" style={{ marginTop: '5px' }}>
                        <Col xs={12}>
                            <Button
                                className={`language-btn w-100 ${formData.negotiable ? 'selected-button' : ''}`}
                                variant="outline-primary"
                                onClick={() =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        negotiable: !prev.negotiable,
                                    }))
                                }
                            >
                                Salary Negotiable
                            </Button>
                        </Col>
                    </Row>

                    <span className="" style={{ fontSize: '14px', color: 'black', marginTop: '0px' }}>Salary as per applicable labour law</span>

                    {/* Navigation Buttons */}
                    <div className="additional-section mb-4" style={{ marginTop: '20px' }}>
                        <div
                            className="d-flex justify-content-between gap-2"
                            style={{ width: "100%" }}
                        >
                            <Button
                                className="nav-btn"
                                style={{ flex: 1 }}
                                variant="secondary"
                                onClick={handleBack}
                            >
                                Back
                            </Button>

                            <Button
                                className="nav-btn"
                                style={{ flex: 1 }}
                                variant="primary"
                                onClick={() => handleSubmit('next')}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
            </Container>
        </>
    );
};

export default AgencyAboutYourself;
